import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { ReleaseAssistantPage } from './components/ReleaseAssistant/ReleaseAssistantPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const outsightColors = {
  orange: '#BF0D0D',
  black: '#101820',
  prussianBlue: '#1E293B',
  paynesGray: '#54596A',
  coolGray: '#999CA8',
  lightBackground: '#F5F7FA',
  white: '#FFFFFF',
} as const;

const outsightLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: outsightColors.prussianBlue,
      dark: '#121A26',
      light: '#2A3952',
      contrastText: outsightColors.white,
    },
    secondary: {
      main: outsightColors.orange,
      dark: '#970A0A',
      light: '#D33A3A',
      contrastText: outsightColors.white,
    },
    error: {
      main: outsightColors.orange,
      contrastText: outsightColors.white,
    },
    warning: {
      main: outsightColors.orange,
      contrastText: outsightColors.white,
    },
    info: {
      main: outsightColors.prussianBlue,
      contrastText: outsightColors.white,
    },
    background: {
      default: outsightColors.lightBackground,
      paper: outsightColors.white,
    },
    text: {
      primary: outsightColors.black,
      secondary: outsightColors.paynesGray,
      disabled: outsightColors.coolGray,
      hint: outsightColors.coolGray,
    },
    border: '#D7DBE2',
    navigation: {
      background: outsightColors.prussianBlue,
      indicator: outsightColors.orange,
      color: '#D7DBE2',
      selectedColor: outsightColors.white,
      navItem: {
        hoverBackground: 'rgba(255, 255, 255, 0.08)',
      },
      submenu: {
        background: '#172130',
      },
    },
    tabbar: {
      indicator: outsightColors.orange,
    },
    link: outsightColors.prussianBlue,
    linkHover: '#2A3952',
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.wave,
    }),
    service: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.round,
    }),
    website: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.round,
    }),
    library: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.wave2,
    }),
    other: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.round,
    }),
    apis: genPageTheme({
      colors: [outsightColors.prussianBlue, '#2A3952'],
      shape: shapes.wave2,
    }),
  },
});

const app = createApp({
  apis,
  themes: [
    {
      id: 'outsight-light',
      title: 'Outsight Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={outsightLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        provider={{
          id: 'google-auth-provider',
          title: 'Google',
          message: 'Sign in with your Outsight Google Workspace account',
          apiRef: googleAuthApiRef,
        }}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <Mermaid />
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <RequirePermission permission={taskCreatePermission}>
          <ScaffolderPage />
        </RequirePermission>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/release-assistant/*" element={<ReleaseAssistantPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
